/**
 * Small building blocks shared by several windows: the coloured dot of a node, a clickable
 * node reference, a titled group, a language picker, a button that asks for a second click
 * before doing something destructive, and the toasts.
 */
import { For, Show, createContext, createSignal, onCleanup, useContext, type Accessor, type JSX } from "solid-js";
import type { Kinds } from "@protocol/Kinds";
import type { Node } from "@protocol/Node";
import { nodeCssColor } from "./categories";
import { nodeKindOf, type ShapeName } from "./kinds";
import { SHAPES } from "./model";
import { toasts } from "./toast";

/**
 * The graph's categories in their order, which picks each one's colour. The shell provides
 * the graph mirror's list; outside it (tests), no category has a colour yet.
 */
export const CategoryOrder = createContext<Accessor<readonly string[]>>(() => []);

/** The colour of a node (its first category's), from the {@link CategoryOrder} around it. */
export function useNodeColor(): (node: Pick<Node, "categories">) => string {
  const order = useContext(CategoryOrder);
  return (node) => nodeCssColor(node, order());
}

/**
 * Every kind that can be used (known, the graph's, the plugins'), so a node's kind can be named
 * here (`Service`, not its id). The shell provides the kinds service's; outside it (tests), no
 * kind is defined and nodes are named after their kind's id.
 */
export const GraphKinds = createContext<Accessor<Kinds | undefined>>(() => undefined);

/** The name of a node's kind (from the {@link GraphKinds} around it). */
export function useKindLabel(): (node: Pick<Node, "kind">) => string {
  const kinds = useContext(GraphKinds);
  return (node) => nodeKindOf(kinds(), node).label;
}

/** The coloured dot of a node (its first category's colour). */
export function Dot(props: { node: Pick<Node, "categories"> }): JSX.Element {
  const color = useNodeColor();
  return <i class="dot" style={{ background: color(props.node) }} />;
}

/** The 16×16 shape a node kind is drawn with, in the current text colour. */
export function KindShape(props: { shape: ShapeName }): JSX.Element {
  return <svg viewBox="0 0 16 16" aria-hidden="true" innerHTML={SHAPES[props.shape] ?? SHAPES.circle} />;
}

/** A clickable row naming a node, with optional edit/remove buttons for the link to it. */
export function NodeRef(props: {
  node: Node;
  extra?: string;
  onOpen: () => void;
  onEditLink?: () => void;
  onRemoveLink?: () => void;
  /** Tooltip of the × button (default "Remove link"). */
  removeTitle?: string;
}): JSX.Element {
  const kindLabel = useKindLabel();
  return (
    <li>
      <button type="button" class="ref" data-go={props.node.id} onClick={props.onOpen}>
        <Dot node={props.node} />
        <span>{props.node.name}</span>
        <em>{props.extra ?? kindLabel(props.node)}</em>
      </button>
      <Show when={props.onEditLink}>
        <button type="button" class="x edit" title="Edit link" aria-label={`Edit link to ${props.node.name}`} onClick={() => props.onEditLink!()}>
          ✎
        </button>
      </Show>
      <Show when={props.onRemoveLink}>
        <button
          type="button"
          class="x"
          title={props.removeTitle ?? "Remove link"}
          aria-label={`${props.removeTitle ?? "Remove link to"} ${props.node.name}`}
          onClick={() => props.onRemoveLink!()}
        >
          ×
        </button>
      </Show>
    </li>
  );
}

/** A titled section with a count, hidden when empty. */
export function Group(props: { title: string; count: number; children: JSX.Element }): JSX.Element {
  return (
    <Show when={props.count > 0}>
      <section class="grp">
        <h3>
          {props.title} <span>{props.count}</span>
        </h3>
        <ul>{props.children}</ul>
      </section>
    </Show>
  );
}

/**
 * A picker for a choice field (a language...): the choices, a blank one, and "Add new …" which
 * shows a text field. Calls `onChange` with the chosen value ("" for none).
 */
export function ChoicePicker(props: {
  id: string;
  /** What is chosen ("Language"), for the label and "Add new language…". */
  label: string;
  value: string;
  blank: string;
  choices: string[];
  onAdd: (name: string) => string;
  onChange: (value: string) => void;
}): JSX.Element {
  const [adding, setAdding] = createSignal(false);
  let input!: HTMLInputElement;
  /** Adds the typed language and selects it. */
  const commit = () => {
    const name = input.value.trim();
    if (!name) return;
    const stored = props.onAdd(name);
    setAdding(false);
    props.onChange(stored);
  };
  return (
    <div class="langbox">
      <label class="sr" for={props.id}>
        {props.label}
      </label>
      <select
        class="select"
        id={props.id}
        value={adding() ? "__new__" : props.value}
        onChange={(e) => {
          if (e.currentTarget.value === "__new__") {
            setAdding(true);
            queueMicrotask(() => input.focus());
          } else {
            setAdding(false);
            props.onChange(e.currentTarget.value);
          }
        }}
      >
        <option value="">{props.blank}</option>
        <For each={props.choices}>{(l) => <option value={l}>{l}</option>}</For>
        {/* The value held, even when it is not among the choices (still shown as chosen). */}
        <Show when={props.value && !props.choices.includes(props.value)}>
          <option value={props.value}>{props.value}</option>
        </Show>
        <option value="__new__">Add new {props.label.toLowerCase()}…</option>
      </select>
      <Show when={adding()}>
        <div class="langnew">
          <input class="field" ref={input} maxlength="40" placeholder={`New ${props.label.toLowerCase()}`} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), commit())} />
          <div class="btnrow">
            <button type="button" class="btn primary" onClick={commit}>
              Add {props.label.toLowerCase()}
            </button>
            <button type="button" class="btn" onClick={() => setAdding(false)}>
              Cancel
            </button>
          </div>
        </div>
      </Show>
    </div>
  );
}

/** A button that needs a second click within a few seconds ("Click again to delete…"). */
export function ArmedButton(props: { label: string; armedLabel: string; onConfirm: () => void; class?: string; id?: string }): JSX.Element {
  const [armed, setArmed] = createSignal(false);
  let timer: ReturnType<typeof setTimeout> | undefined;
  onCleanup(() => clearTimeout(timer));
  return (
    <button
      type="button"
      id={props.id}
      class={`btn danger ${props.class ?? ""}`}
      classList={{ armed: armed() }}
      onClick={() => {
        if (!armed()) {
          setArmed(true);
          timer = setTimeout(() => setArmed(false), 3500);
          return;
        }
        clearTimeout(timer);
        setArmed(false);
        props.onConfirm();
      }}
    >
      {armed() ? props.armedLabel : props.label}
    </button>
  );
}

/** Formats a timestamp (ms) as "15 Oct · 21:30". */
export function formatDate(ms: number): string {
  const d = new Date(ms);
  return `${d.toLocaleDateString(undefined, { day: "numeric", month: "short" })} · ${d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" })}`;
}

/** Copies text to the clipboard; reports whether it worked. */
export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/** The short messages of {@link toast}, in the page's corner (the editor and the app pages). */
export function Toasts(): JSX.Element {
  return (
    <div class="toasts" role="status">
      <For each={toasts()}>{(t) => <div class={`toast ${t.kind}`}>{t.text}</div>}</For>
    </div>
  );
}
